import { img, LOGO_W, LOGO_H } from "@/assets/images";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, ArrowRight, Instagram, Facebook, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { SITE } from "@/lib/site";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-accent text-accent-foreground">
      {/* CTA Banner */}
      <div className="bg-primary relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="container-max px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready for a spotless space?
              </h2>
              <p className="text-primary-foreground/80 text-sm mt-1">
                Get your free, no-pressure cleaning estimate today.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={openQuoteForm}
                className="gap-2 rounded-xl font-bold"
              >
                Get Started <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                asChild
                variant="secondary"
                size="lg"
                className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
              >
                <a href={SITE.phoneHref}>
                  <Phone className="h-4 w-4" /> Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer content */}
      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="mb-4 inline-flex items-center justify-center rounded-2xl bg-white p-3 shadow-premium">
              <img src={img.logo} alt="Good Choice Cleaning & Multi Services" className="h-12 w-auto" width={LOGO_W} height={LOGO_H} loading="lazy" decoding="async" />
            </div>
            <p className="text-accent-foreground/75 text-sm leading-relaxed">
              Trusted residential and commercial cleaning across Harrisonburg and the Shenandoah Valley.
              Dependable crews, honest pricing, and a spotless finish every time.
            </p>
            <div className="mt-4 bg-secondary/10 border border-secondary/20 rounded-xl px-4 py-3">
              <p className="text-secondary-bright font-semibold text-sm">
                🎁 {SITE.referralOffer}
              </p>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-accent-foreground/80 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-accent-foreground/80 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: "Services", to: "/services" },
                { label: "Locations", to: "/locations" },
                { label: "Gallery", to: "/gallery" },
                { label: "Blog", to: "/blog" },
                { label: "About", to: "/about" },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="footer-link-underline text-accent-foreground/75 hover:text-primary text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Contact
            </h3>
            <ul className="space-y-3 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-primary" />
                <a href={SITE.phoneHref} className="hover:text-primary transition-colors">{SITE.phoneDisplay}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-primary" />
                <a href={SITE.emailHref} className="hover:text-primary transition-colors break-all">{SITE.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                <span>Serving {SITE.serviceArea}</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg tracking-tight mb-4 text-accent-foreground/80 font-bold">
              Hours
            </h3>
            <ul className="space-y-2 text-sm text-accent-foreground/80">
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-primary" />
                Monday – Friday
              </li>
              <li className="pl-6">7:00 AM – 7:00 PM</li>
              <li className="flex items-center gap-2 pt-1">
                <Clock className="h-4 w-4 shrink-0 text-primary" />
                Saturday – Sunday
              </li>
              <li className="pl-6">9:00 AM – 5:00 PM</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-accent-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-accent-foreground/60 text-xs">
            © {new Date().getFullYear()} {SITE.name}. All rights reserved.
          </p>
          <span className="text-accent-foreground/60 text-xs">Harrisonburg, VA · {SITE.region}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
